export type Language = 'en' | 'cs';

const translations: Record<Language, Record<string, string>> = {
  en: {
    // === MODULES / NAVIGATION ===
    'nav.dashboard': 'Dashboard',
    'nav.ledger': 'Ledger',
    'nav.accounts_payable': 'Accounts Payable',
    'nav.accounts_receivable': 'Accounts Receivable',
    'nav.hr': 'Human Resources',
    'nav.inventory': 'Inventory',
    'nav.product_management': 'Product Management',
    'nav.settings': 'Settings',
    'nav.chart_of_accounts': 'Chart of Accounts',
    'nav.journal_entries': 'Journal Entries',
    'nav.financial_reports': 'Financial Reports',
    'nav.purchase_orders': 'Purchase Orders',
    'nav.purchase_invoices': 'Purchase Invoices',
    'nav.suppliers': 'Suppliers',
    'nav.goods_receipts': 'Goods Receipts',
    'nav.sales_orders': 'Sales Orders',
    'nav.invoices': 'Invoices',
    'nav.customers': 'Customers',
    'nav.employees': 'Employees',
    'nav.products': 'Products',

    // === COMMON ===
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.update': 'Update',
    'common.back': 'Back',
    'common.back_to_list': 'Back to List',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.confirm': 'Confirm',
    'common.loading': 'Loading...',
    'common.saving': 'Saving...',
    'common.sending': 'Sending...',
    'common.add_line': 'Add Line',
    'common.confirm_delete': 'Confirm Delete',
    'common.welcome': 'Welcome',
    'common.logout': 'Logout',
    'common.total': 'Total',
    'common.subtotal': 'Subtotal',
    'common.tax': 'Tax',
    'common.notes': 'Notes',
    'common.date': 'Date',
    'common.description': 'Description',
    'common.quantity': 'Quantity',
    'common.unit_price': 'Unit Price',
    'common.line_total': 'Line Total',
    'common.currency': 'Currency',
    'common.amount': 'Amount',
    'common.balance': 'Balance',
    'common.code': 'Code',
    'common.name': 'Name',
    'common.type': 'Type',
    'common.phone': 'Phone',
    'common.email': 'Email',
    'common.address': 'Address',
    'common.city': 'City',
    'common.country': 'Country',
    'common.tax_number': 'Tax Number',
    'common.contact_person': 'Contact Person',
    'common.payment_terms': 'Payment Terms',
    'common.group': 'Group',
    'common.select': 'Select',
    'common.none': '— None —',
    'common.not_balanced': 'NOT BALANCED',
    'common.totals': 'Totals',
    'common.view': 'View',
    'common.mark_paid': 'Mark as Paid',
    'common.mark_overdue': 'Mark Overdue',
    'common.record_payment': 'Record Payment',

    // === LOGIN ===
    'login.title': 'ERP System Login',
    'login.subtitle': 'Sign in to your account',
    'login.username': 'Username',
    'login.password': 'Password',
    'login.submit': 'Login',
    'login.error': 'Invalid credentials',

    // === DASHBOARD ===
    'dashboard.title': 'Dashboard',
    'dashboard.total_products': 'Total Products',
    'dashboard.total_customers': 'Total Customers',
    'dashboard.total_invoices': 'Total Invoices',
    'dashboard.total_employees': 'Total Employees',
    'dashboard.recent_orders': 'Recent Sales Orders',
    'dashboard.recent_invoices': 'Recent Invoices',

    // === PRODUCTS ===
    'products.title': 'Products',
    'products.new': 'New Product',
    'products.edit': 'Edit Product',
    'products.sku': 'SKU',
    'products.product_name': 'Product Name',
    'products.category': 'Category',
    'products.unit_price': 'Unit Price',
    'products.cost_price': 'Cost Price',
    'products.reorder_level': 'Reorder Level',
    'products.unit': 'Unit',
    'products.delete_confirm': 'Are you sure you want to delete product "{name}"?',

    // === CUSTOMERS ===
    'customers.title': 'Customers',
    'customers.new': 'New Customer',
    'customers.edit': 'Edit Customer',
    'customers.customer_code': 'Customer Code',
    'customers.customer_name': 'Customer Name',
    'customers.customer_type': 'Customer Type',
    'customers.customer_group': 'Customer Group',
    'customers.credit_limit': 'Credit Limit',
    'customers.business': 'Business',
    'customers.individual': 'Individual',
    'customers.delete_confirm': 'Are you sure you want to delete customer "{name}"?',
    'customers.net30': 'Net 30',
    'customers.net60': 'Net 60',
    'customers.net90': 'Net 90',
    'customers.cod': 'Cash on Delivery',

    // === SUPPLIERS ===
    'suppliers.title': 'Suppliers',
    'suppliers.new': 'New Supplier',
    'suppliers.edit': 'Edit Supplier',
    'suppliers.supplier_code': 'Supplier Code',
    'suppliers.supplier_name': 'Supplier Name',
    'suppliers.supplier_group': 'Supplier Group',
    'suppliers.basic_info': 'Basic Information',
    'suppliers.contact_details': 'Contact Details',
    'suppliers.address_info': 'Address',
    'suppliers.business_details': 'Business Details',
    'suppliers.orders': 'Orders',
    'suppliers.delete_confirm': 'Are you sure you want to delete this supplier?',
    'suppliers.create': 'Create Supplier',
    'suppliers.update': 'Update Supplier',

    // === SALES ORDERS ===
    'sales_orders.title': 'Sales Orders',
    'sales_orders.new': 'New Sales Order',
    'sales_orders.order_lines': 'Order Lines',
    'sales_orders.customer': 'Customer',
    'sales_orders.shipping_address': 'Shipping Address',
    'sales_orders.billing_address': 'Billing Address',
    'sales_orders.product': 'Product',
    'sales_orders.discount': 'Discount',
    'sales_orders.tax_pct': 'Tax %',
    'sales_orders.create': 'Create Sales Order',
    'sales_orders.generate_invoice': 'Generate Invoice',
    'sales_orders.confirm': 'Confirm',
    'sales_orders.process': 'Process',
    'sales_orders.ship': 'Ship',
    'sales_orders.deliver': 'Deliver',

    // === INVOICES ===
    'invoices.title': 'Invoices',
    'invoices.new': 'New Invoice',
    'invoices.edit': 'Edit Invoice',
    'invoices.invoice_number': 'Invoice #',
    'invoices.due_date': 'Due Date',
    'invoices.line_items': 'Line Items',
    'invoices.download_pdf': 'Download PDF',
    'invoices.send_email': 'Send Email',
    'invoices.record_payment': 'Record Payment',
    'invoices.mark_paid': 'Mark as Paid',
    'invoices.mark_overdue': 'Mark Overdue',
    'invoices.email_recipient': 'Recipient Email',
    'invoices.email_name': 'Recipient Name',
    'invoices.email_message': 'Custom Message (optional)',
    'invoices.send_invoice': 'Send Invoice',
    'invoices.payment_amount': 'Amount',
    'invoices.payment_date': 'Payment Date',
    'invoices.payment_method': 'Payment Method',
    'invoices.payment_reference': 'Reference / Note',
    'invoices.bank_transfer': 'Bank Transfer',
    'invoices.cash': 'Cash',
    'invoices.check': 'Check',
    'invoices.credit_card': 'Credit Card',
    'invoices.other': 'Other',
    'invoices.balance_due': 'Balance due',

    // === PURCHASE ORDERS ===
    'purchase_orders.title': 'Purchase Orders',
    'purchase_orders.new': 'New Purchase Order',
    'purchase_orders.order_date': 'Order Date',
    'purchase_orders.supplier': 'Supplier',
    'purchase_orders.expected_delivery': 'Expected Delivery',
    'purchase_orders.order_lines': 'Order Lines',
    'purchase_orders.received': 'Received',
    'purchase_orders.create': 'Create Purchase Order',
    'purchase_orders.generate_bill': 'Generate Bill',
    'purchase_orders.generate_bill_confirm': 'Generate bill from {orderNumber}?',
    'purchase_orders.approve': 'Approve',
    'purchase_orders.mark_ordered': 'Mark Ordered',
    'purchase_orders.po_number': 'PO Number',
    'purchase_orders.items': 'Items',

    // === PURCHASE INVOICES ===
    'purchase_invoices.title': 'Purchase Invoices (Bills)',
    'purchase_invoices.new': 'New Bill',
    'purchase_invoices.from_po': 'From Purchase Order (optional)',
    'purchase_invoices.standalone': '— None (standalone bill) —',
    'purchase_invoices.supplier_invoice': 'Supplier Invoice #',
    'purchase_invoices.invoice_date': 'Invoice Date',
    'purchase_invoices.bill_lines': 'Bill Lines',
    'purchase_invoices.create': 'Create Bill',
    'purchase_invoices.vendor_ref': 'Vendor Ref',
    'purchase_invoices.due_date': 'Due Date',
    'purchase_invoices.record_payment': 'Record Payment',
    'purchase_invoices.payment_amount': 'Amount',
    'purchase_invoices.payment_date': 'Payment Date',
    'purchase_invoices.payment_method': 'Payment Method',
    'purchase_invoices.payment_reference': 'Reference / Note',
    'purchase_invoices.info': 'Optionally select a Purchase Order to auto-fill the bill lines, or create a standalone bill.',

    // === GOODS RECEIPTS ===
    'goods_receipts.title': 'Goods Receipts',
    'goods_receipts.new': 'New Goods Receipt',
    'goods_receipts.receipt_number': 'Receipt #',
    'goods_receipts.po_number': 'PO Number',
    'goods_receipts.items': 'Items',
    'goods_receipts.total_qty': 'Total Qty',

    // === EMPLOYEES ===
    'employees.title': 'Employees',
    'employees.new': 'New Employee',
    'employees.edit': 'Edit Employee',
    'employees.employee_number': 'Employee Number',
    'employees.first_name': 'First Name',
    'employees.last_name': 'Last Name',
    'employees.department': 'Department',
    'employees.position': 'Position',
    'employees.salary': 'Salary',
    'employees.hire_date': 'Hire Date',
    'employees.date_of_birth': 'Date of Birth',
    'employees.employment_type': 'Employment Type',
    'employees.full_time': 'Full Time',
    'employees.part_time': 'Part Time',
    'employees.contract': 'Contract',
    'employees.intern': 'Intern',

    // === ACCOUNTING ===
    'accounts.title': 'Chart of Accounts',
    'accounts.new': 'New Account',
    'accounts.edit': 'Edit Account',
    'accounts.account_code': 'Account Code',
    'accounts.account_name': 'Account Name',
    'accounts.account_type': 'Account Type',
    'accounts.asset': 'Asset',
    'accounts.liability': 'Liability',
    'accounts.equity': 'Equity',
    'accounts.revenue': 'Revenue',
    'accounts.expense': 'Expense',
    'accounts.normal_balance': 'Normal Balance',
    'accounts.parent_account': 'Parent Account',
    'accounts.balance': 'Balance',
    'accounts.delete_confirm': 'Are you sure you want to delete this account?',

    'journal.title': 'Journal Entries',
    'journal.new': 'New Journal Entry',
    'journal.entry_number': 'Entry #',
    'journal.entry_date': 'Entry Date',
    'journal.reference': 'Reference',
    'journal.entry_lines': 'Entry Lines',
    'journal.account': 'Account',
    'journal.debit': 'Debit',
    'journal.credit': 'Credit',
    'journal.create': 'Create Journal Entry',
    'journal.min_lines': 'At least two lines are required',
    'journal.post': 'Post',
    'journal.void': 'Void',
    'journal.lines': 'Lines',

    'reports.title': 'Financial Reports',
    'reports.trial_balance': 'Trial Balance',
    'reports.income_statement': 'Income Statement',
    'reports.balance_sheet': 'Balance Sheet',
    'reports.period': 'Period',
    'reports.from_date': 'From Date',
    'reports.to_date': 'To Date',
    'reports.generate': 'Generate Report',

    // === SETTINGS ===
    'settings.title': 'Settings',
    'settings.company_info': 'Company Info',
    'settings.currencies': 'Currencies',
    'settings.exchange_rates': 'Exchange Rates',
    'settings.email_config': 'Email',
    'settings.integrations': 'Integrations',
    'settings.company_name': 'Company Name',
    'settings.company_address': 'Company Address',
    'settings.company_phone': 'Company Phone',
    'settings.company_email': 'Company Email',
    'settings.company_tax_number': 'Company Tax Number',
    'settings.bank_name': 'Bank Name',
    'settings.bank_account': 'Bank Account',
    'settings.invoice_prefix': 'Invoice Prefix',
    'settings.payment_terms_default': 'Default Payment Terms',
    'settings.default_language': 'Default Language',
    'settings.save_settings': 'Save Settings',
    'settings.language_english': 'English',
    'settings.language_czech': 'Czech (Čeština)',

    // === SETTINGS (extended) ===
    'settings.company_details': 'Company Details',
    'settings.address_section': 'Address',
    'settings.contact_section': 'Contact',
    'settings.banking_section': 'Banking Details',
    'settings.invoice_defaults': 'Invoice Defaults',
    'settings.company_city': 'City',
    'settings.company_country': 'Country',
    'settings.company_website': 'Website',
    'settings.company_registration': 'Registration Number',
    'settings.bank_swift': 'SWIFT / BIC',
    'settings.invoice_footer': 'Invoice Footer Text',
    'settings.saved_ok': 'Settings saved successfully!',
    'settings.groups': 'Groups',
    'settings.add_currency': 'Add Currency',
    'settings.edit_currency': 'Edit Currency',
    'settings.currency_code': 'Code (e.g. USD)',
    'settings.currency_symbol': 'Symbol (e.g. $)',
    'settings.currency_name': 'Name (e.g. US Dollar)',
    'settings.currency_decimals': 'Decimal Places',
    'settings.currency_default': 'Default Currency',
    'settings.no_currencies': 'No currencies configured. Add your first currency to get started.',
    'settings.add_rate': 'Add Rate',
    'settings.from_currency': 'From Currency',
    'settings.to_currency': 'To Currency',
    'settings.rate': 'Rate',
    'settings.effective_date': 'Effective Date',
    'settings.no_rates': 'No exchange rates configured.',
    'settings.min_currencies': 'Add at least 2 currencies in the Currencies tab before adding exchange rates.',
    'settings.delete_currency_confirm': 'Delete this currency?',
    'settings.delete_rate_confirm': 'Delete this exchange rate?',

    // === GROUPS ===
    'groups.supplier_groups': 'Supplier Groups',
    'groups.customer_groups': 'Customer Groups',
    'groups.new_group': 'New Group',
    'groups.edit_group': 'Edit Group',
    'groups.group_code': 'Group Code',
    'groups.group_name': 'Group Name / Description',
    'groups.delete_confirm': 'Are you sure you want to delete this group?',
    'groups.no_groups': 'No groups defined yet.',

    // === NUMBER SEQUENCES ===
    'settings.number_sequences': 'Number Sequences',
    'settings.dimensions': 'Financial Dimensions',
    'settings.companies': 'Companies',
    'company.code': 'Code',
    'company.name': 'Name',
    'company.new': 'New Company',
    'company.edit': 'Edit Company',
    'company.city': 'City',
    'company.country': 'Country',
    'company.address': 'Address',
    'company.phone': 'Phone',
    'company.email': 'Email',
    'company.website': 'Website',
    'company.tax_id': 'Tax ID',
    'company.registration_number': 'Registration Number',
    'company.bank_name': 'Bank Name',
    'company.bank_account': 'Bank Account',
    'company.bank_swift': 'SWIFT/BIC',
    'company.current': 'Current',
    'company.switch': 'Switch',
    'company.set_default': 'Set as Default',
    'company.default_set': 'Default company updated',
    'company.cannot_delete_current': 'Cannot delete the current company',
    'number_sequences.title': 'Number Sequences',
    'number_sequences.new': 'New Sequence',
    'number_sequences.edit': 'Edit Sequence',
    'number_sequences.type': 'Document Type',
    'number_sequences.prefix': 'Prefix',
    'number_sequences.suffix': 'Suffix',
    'number_sequences.next_number': 'Next Number',
    'number_sequences.padding': 'Padding',
    'number_sequences.padding_help': 'Number of digits (e.g. 5 → 00001)',
    'number_sequences.include_year': 'Include Year',
    'number_sequences.include_month': 'Include Month',
    'number_sequences.year': 'Year',
    'number_sequences.month': 'Month',
    'number_sequences.preview': 'Preview',
    'number_sequences.empty': 'No number sequences configured. Documents will use fallback numbering.',
    'number_sequences.auto_generate': 'Leave empty to auto-generate',
    'number_sequences.types.customer': 'Customer',
    'number_sequences.types.supplier': 'Supplier',
    'number_sequences.types.sales_order': 'Sales Order',
    'number_sequences.types.purchase_order': 'Purchase Order',
    'number_sequences.types.sales_invoice': 'Sales Invoice',
    'number_sequences.types.purchase_invoice': 'Purchase Invoice',
    'number_sequences.types.goods_receipt': 'Goods Receipt',
    'number_sequences.types.journal_entry': 'Journal Entry',

    // === FINANCIAL DIMENSIONS ===
    'dimensions.title': 'Financial Dimensions',
    'dimensions.new': 'New Dimension',
    'dimensions.edit': 'Edit Dimension',
    'dimensions.values_label': 'Values',
    'dimensions.new_value': 'New Value',
    'dimensions.edit_value': 'Edit Value',
    'dimensions.no_values': 'No values defined',
    'dimensions.empty': 'No financial dimensions configured.',
    'dimensions.required': 'Required',
    'dimensions.sort_order': 'Sort Order',

    // === POSTING PROFILES ===
    'posting.title': 'Posting Profiles',
    'posting.party_profiles': 'Customer & Supplier Profiles',
    'posting.product_profiles': 'Product Profiles',
    'posting.product_posting_profile': 'Product Posting Profile',
    'posting.new': 'New Posting Profile',
    'posting.edit': 'Edit Posting Profile',
    'posting.party_type': 'Party Type',
    'posting.document_type': 'Document Type',
    'posting.profile_name': 'Profile Name',
    'posting.is_default': 'Default',
    'posting.customer_group': 'Customer Group (optional)',
    'posting.supplier_group': 'Supplier Group (optional)',
    'posting.receivable_account': 'Receivable (Debt) Account',
    'posting.payable_account': 'Payable (Liability) Account',
    'posting.advance_account': 'Advance Payment Account',
    'posting.revenue_account': 'Revenue Account',
    'posting.expense_account': 'Expense Account',
    'posting.tax_account': 'Tax Account',
    'posting.vat_account': 'VAT Account',
    'posting.bank_account': 'Bank Account',
    'posting.no_profiles': 'No party posting profiles configured.',
    'posting.no_product_profiles': 'No product posting profiles configured.',
    'posting.delete_confirm': 'Delete this posting profile?',
    'posting.sales_invoice': 'Sales Invoice',
    'posting.purchase_invoice': 'Purchase Invoice',
    'posting.payment_received': 'Payment Received',
    'posting.payment_made': 'Payment Made',
    'posting.post_to_ledger': 'Post to Ledger',
    'posting.post_confirm': 'Post this invoice to the ledger? This will create a journal entry.',
    'posting.posted_ok': 'Successfully posted to ledger',
    'posting.already_posted': 'Already posted',

    // === SUBLEDGER ===
    'subledger.title': 'Subledger',
    'subledger.entries': 'Subledger Entries',
    'subledger.balances': 'Balances',
    'subledger.aging': 'Aging Report',
    'subledger.party_type': 'Party Type',
    'subledger.party': 'Party',
    'subledger.document': 'Document',
    'subledger.entry_type': 'Entry Type',
    'subledger.debit': 'Debit',
    'subledger.credit': 'Credit',
    'subledger.running_balance': 'Balance',
    'subledger.customer': 'Customer',
    'subledger.supplier': 'Supplier',
    'subledger.all': 'All',
    'subledger.no_entries': 'No subledger entries.',
    'subledger.total_debit': 'Total Debit',
    'subledger.total_credit': 'Total Credit',
    'subledger.net_balance': 'Net Balance',
    'subledger.transactions': 'Transactions',
    'subledger.current': 'Current',
    'subledger.days_1_30': '1-30 Days',
    'subledger.days_31_60': '31-60 Days',
    'subledger.days_61_90': '61-90 Days',
    'subledger.over_90': 'Over 90 Days',
    'subledger.no_aging': 'No outstanding balances.',
  },

  cs: {
    // === MODULES / NAVIGATION ===
    'nav.dashboard': 'Přehled',
    'nav.ledger': 'Účetnictví',
    'nav.accounts_payable': 'Závazky',
    'nav.accounts_receivable': 'Pohledávky',
    'nav.hr': 'Lidské zdroje',
    'nav.inventory': 'Sklad',
    'nav.product_management': 'Správa produktů',
    'nav.settings': 'Nastavení',
    'nav.chart_of_accounts': 'Účtová osnova',
    'nav.journal_entries': 'Účetní zápisy',
    'nav.financial_reports': 'Finanční výkazy',
    'nav.purchase_orders': 'Objednávky přijaté',
    'nav.purchase_invoices': 'Faktury přijaté',
    'nav.suppliers': 'Dodavatelé',
    'nav.goods_receipts': 'Příjemky',
    'nav.sales_orders': 'Prodejní objednávky',
    'nav.invoices': 'Faktury vydané',
    'nav.customers': 'Zákazníci',
    'nav.employees': 'Zaměstnanci',
    'nav.products': 'Produkty',

    // === COMMON ===
    'common.save': 'Uložit',
    'common.cancel': 'Zrušit',
    'common.delete': 'Smazat',
    'common.edit': 'Upravit',
    'common.create': 'Vytvořit',
    'common.update': 'Aktualizovat',
    'common.back': 'Zpět',
    'common.back_to_list': 'Zpět na seznam',
    'common.search': 'Hledat',
    'common.filter': 'Filtr',
    'common.actions': 'Akce',
    'common.status': 'Stav',
    'common.active': 'Aktivní',
    'common.inactive': 'Neaktivní',
    'common.yes': 'Ano',
    'common.no': 'Ne',
    'common.confirm': 'Potvrdit',
    'common.loading': 'Načítání...',
    'common.saving': 'Ukládání...',
    'common.sending': 'Odesílání...',
    'common.add_line': 'Přidat řádek',
    'common.confirm_delete': 'Potvrdit smazání',
    'common.welcome': 'Vítejte',
    'common.logout': 'Odhlásit',
    'common.total': 'Celkem',
    'common.subtotal': 'Mezisoučet',
    'common.tax': 'DPH',
    'common.notes': 'Poznámky',
    'common.date': 'Datum',
    'common.description': 'Popis',
    'common.quantity': 'Množství',
    'common.unit_price': 'Jednotková cena',
    'common.line_total': 'Celkem řádek',
    'common.currency': 'Měna',
    'common.amount': 'Částka',
    'common.balance': 'Zůstatek',
    'common.code': 'Kód',
    'common.name': 'Název',
    'common.type': 'Typ',
    'common.phone': 'Telefon',
    'common.email': 'E-mail',
    'common.address': 'Adresa',
    'common.city': 'Město',
    'common.country': 'Země',
    'common.tax_number': 'DIČ',
    'common.contact_person': 'Kontaktní osoba',
    'common.payment_terms': 'Platební podmínky',
    'common.group': 'Skupina',
    'common.select': 'Vybrat',
    'common.none': '— Žádná —',
    'common.not_balanced': 'NESOUHLASÍ',
    'common.totals': 'Součty',
    'common.view': 'Zobrazit',
    'common.mark_paid': 'Označit zaplaceno',
    'common.mark_overdue': 'Označit po splatnosti',
    'common.record_payment': 'Zaznamenat platbu',

    // === LOGIN ===
    'login.title': 'Přihlášení do ERP',
    'login.subtitle': 'Přihlaste se ke svému účtu',
    'login.username': 'Uživatelské jméno',
    'login.password': 'Heslo',
    'login.submit': 'Přihlásit',
    'login.error': 'Neplatné přihlašovací údaje',

    // === DASHBOARD ===
    'dashboard.title': 'Přehled',
    'dashboard.total_products': 'Celkem produktů',
    'dashboard.total_customers': 'Celkem zákazníků',
    'dashboard.total_invoices': 'Celkem faktur',
    'dashboard.total_employees': 'Celkem zaměstnanců',
    'dashboard.recent_orders': 'Poslední prodejní objednávky',
    'dashboard.recent_invoices': 'Poslední faktury',

    // === PRODUCTS ===
    'products.title': 'Produkty',
    'products.new': 'Nový produkt',
    'products.edit': 'Upravit produkt',
    'products.sku': 'SKU',
    'products.product_name': 'Název produktu',
    'products.category': 'Kategorie',
    'products.unit_price': 'Prodejní cena',
    'products.cost_price': 'Nákupní cena',
    'products.reorder_level': 'Min. množství',
    'products.unit': 'Jednotka',
    'products.delete_confirm': 'Opravdu chcete smazat produkt „{name}"?',

    // === CUSTOMERS ===
    'customers.title': 'Zákazníci',
    'customers.new': 'Nový zákazník',
    'customers.edit': 'Upravit zákazníka',
    'customers.customer_code': 'Kód zákazníka',
    'customers.customer_name': 'Název zákazníka',
    'customers.customer_type': 'Typ zákazníka',
    'customers.customer_group': 'Skupina zákazníků',
    'customers.credit_limit': 'Kreditní limit',
    'customers.business': 'Firma',
    'customers.individual': 'Fyzická osoba',
    'customers.delete_confirm': 'Opravdu chcete smazat zákazníka „{name}"?',
    'customers.net30': 'Splatnost 30 dní',
    'customers.net60': 'Splatnost 60 dní',
    'customers.net90': 'Splatnost 90 dní',
    'customers.cod': 'Dobírka',

    // === SUPPLIERS ===
    'suppliers.title': 'Dodavatelé',
    'suppliers.new': 'Nový dodavatel',
    'suppliers.edit': 'Upravit dodavatele',
    'suppliers.supplier_code': 'Kód dodavatele',
    'suppliers.supplier_name': 'Název dodavatele',
    'suppliers.supplier_group': 'Skupina dodavatelů',
    'suppliers.basic_info': 'Základní údaje',
    'suppliers.contact_details': 'Kontaktní údaje',
    'suppliers.address_info': 'Adresa',
    'suppliers.business_details': 'Obchodní údaje',
    'suppliers.orders': 'Objednávky',
    'suppliers.delete_confirm': 'Opravdu chcete smazat tohoto dodavatele?',
    'suppliers.create': 'Vytvořit dodavatele',
    'suppliers.update': 'Aktualizovat dodavatele',

    // === SALES ORDERS ===
    'sales_orders.title': 'Prodejní objednávky',
    'sales_orders.new': 'Nová prodejní objednávka',
    'sales_orders.order_lines': 'Řádky objednávky',
    'sales_orders.customer': 'Zákazník',
    'sales_orders.shipping_address': 'Dodací adresa',
    'sales_orders.billing_address': 'Fakturační adresa',
    'sales_orders.product': 'Produkt',
    'sales_orders.discount': 'Sleva',
    'sales_orders.tax_pct': 'DPH %',
    'sales_orders.create': 'Vytvořit objednávku',
    'sales_orders.generate_invoice': 'Vystavit fakturu',
    'sales_orders.confirm': 'Potvrdit',
    'sales_orders.process': 'Zpracovat',
    'sales_orders.ship': 'Odeslat',
    'sales_orders.deliver': 'Doručit',

    // === INVOICES ===
    'invoices.title': 'Faktury vydané',
    'invoices.new': 'Nová faktura',
    'invoices.edit': 'Upravit fakturu',
    'invoices.invoice_number': 'Číslo faktury',
    'invoices.due_date': 'Datum splatnosti',
    'invoices.line_items': 'Položky faktury',
    'invoices.download_pdf': 'Stáhnout PDF',
    'invoices.send_email': 'Odeslat e-mailem',
    'invoices.record_payment': 'Zaznamenat platbu',
    'invoices.mark_paid': 'Označit jako zaplaceno',
    'invoices.mark_overdue': 'Označit po splatnosti',
    'invoices.email_recipient': 'E-mail příjemce',
    'invoices.email_name': 'Jméno příjemce',
    'invoices.email_message': 'Vlastní zpráva (volitelné)',
    'invoices.send_invoice': 'Odeslat fakturu',
    'invoices.payment_amount': 'Částka',
    'invoices.payment_date': 'Datum platby',
    'invoices.payment_method': 'Způsob platby',
    'invoices.payment_reference': 'Reference / Poznámka',
    'invoices.bank_transfer': 'Bankovní převod',
    'invoices.cash': 'Hotovost',
    'invoices.check': 'Šek',
    'invoices.credit_card': 'Platební karta',
    'invoices.other': 'Jiné',
    'invoices.balance_due': 'Zbývá k úhradě',

    // === PURCHASE ORDERS ===
    'purchase_orders.title': 'Objednávky přijaté',
    'purchase_orders.new': 'Nová objednávka',
    'purchase_orders.order_date': 'Datum objednávky',
    'purchase_orders.supplier': 'Dodavatel',
    'purchase_orders.expected_delivery': 'Očekávané dodání',
    'purchase_orders.order_lines': 'Řádky objednávky',
    'purchase_orders.received': 'Přijato',
    'purchase_orders.create': 'Vytvořit objednávku',
    'purchase_orders.generate_bill': 'Vystavit fakturu',
    'purchase_orders.generate_bill_confirm': 'Vystavit fakturu z {orderNumber}?',
    'purchase_orders.approve': 'Schválit',
    'purchase_orders.mark_ordered': 'Označit objednáno',
    'purchase_orders.po_number': 'Číslo objednávky',
    'purchase_orders.items': 'Položky',

    // === PURCHASE INVOICES ===
    'purchase_invoices.title': 'Faktury přijaté',
    'purchase_invoices.new': 'Nová přijatá faktura',
    'purchase_invoices.from_po': 'Z objednávky (volitelné)',
    'purchase_invoices.standalone': '— Žádná (samostatná faktura) —',
    'purchase_invoices.supplier_invoice': 'Číslo faktury dodavatele',
    'purchase_invoices.invoice_date': 'Datum fakturace',
    'purchase_invoices.bill_lines': 'Řádky faktury',
    'purchase_invoices.create': 'Vytvořit fakturu',
    'purchase_invoices.vendor_ref': 'Ref. dodavatele',
    'purchase_invoices.due_date': 'Datum splatnosti',
    'purchase_invoices.record_payment': 'Zaznamenat platbu',
    'purchase_invoices.payment_amount': 'Částka',
    'purchase_invoices.payment_date': 'Datum platby',
    'purchase_invoices.payment_method': 'Způsob platby',
    'purchase_invoices.payment_reference': 'Reference / Poznámka',
    'purchase_invoices.info': 'Volitelně vyberte objednávku pro automatické vyplnění řádků, nebo vytvořte samostatnou fakturu.',

    // === GOODS RECEIPTS ===
    'goods_receipts.title': 'Příjemky',
    'goods_receipts.new': 'Nová příjemka',
    'goods_receipts.receipt_number': 'Číslo příjemky',
    'goods_receipts.po_number': 'Číslo objednávky',
    'goods_receipts.items': 'Položky',
    'goods_receipts.total_qty': 'Celkové množství',

    // === EMPLOYEES ===
    'employees.title': 'Zaměstnanci',
    'employees.new': 'Nový zaměstnanec',
    'employees.edit': 'Upravit zaměstnance',
    'employees.employee_number': 'Osobní číslo',
    'employees.first_name': 'Jméno',
    'employees.last_name': 'Příjmení',
    'employees.department': 'Oddělení',
    'employees.position': 'Pozice',
    'employees.salary': 'Plat',
    'employees.hire_date': 'Datum nástupu',
    'employees.date_of_birth': 'Datum narození',
    'employees.employment_type': 'Typ úvazku',
    'employees.full_time': 'Plný úvazek',
    'employees.part_time': 'Částečný úvazek',
    'employees.contract': 'Smlouva',
    'employees.intern': 'Stáž',

    // === ACCOUNTING ===
    'accounts.title': 'Účtová osnova',
    'accounts.new': 'Nový účet',
    'accounts.edit': 'Upravit účet',
    'accounts.account_code': 'Číslo účtu',
    'accounts.account_name': 'Název účtu',
    'accounts.account_type': 'Typ účtu',
    'accounts.asset': 'Aktiva',
    'accounts.liability': 'Pasiva',
    'accounts.equity': 'Vlastní kapitál',
    'accounts.revenue': 'Výnosy',
    'accounts.expense': 'Náklady',
    'accounts.normal_balance': 'Normální zůstatek',
    'accounts.parent_account': 'Nadřazený účet',
    'accounts.balance': 'Zůstatek',
    'accounts.delete_confirm': 'Opravdu chcete smazat tento účet?',

    'journal.title': 'Účetní zápisy',
    'journal.new': 'Nový účetní zápis',
    'journal.entry_number': 'Číslo zápisu',
    'journal.entry_date': 'Datum zápisu',
    'journal.reference': 'Reference',
    'journal.entry_lines': 'Řádky zápisu',
    'journal.account': 'Účet',
    'journal.debit': 'Má dáti',
    'journal.credit': 'Dal',
    'journal.create': 'Vytvořit účetní zápis',
    'journal.min_lines': 'Jsou vyžadovány alespoň dva řádky',
    'journal.post': 'Zaúčtovat',
    'journal.void': 'Stornovat',
    'journal.lines': 'Řádky',

    'reports.title': 'Finanční výkazy',
    'reports.trial_balance': 'Obratová předvaha',
    'reports.income_statement': 'Výsledovka',
    'reports.balance_sheet': 'Rozvaha',
    'reports.period': 'Období',
    'reports.from_date': 'Od data',
    'reports.to_date': 'Do data',
    'reports.generate': 'Vygenerovat výkaz',

    // === SETTINGS ===
    'settings.title': 'Nastavení',
    'settings.company_info': 'Údaje o firmě',
    'settings.currencies': 'Měny',
    'settings.exchange_rates': 'Kurzy měn',
    'settings.email_config': 'E-mail',
    'settings.integrations': 'Integrace',
    'settings.company_name': 'Název firmy',
    'settings.company_address': 'Adresa firmy',
    'settings.company_phone': 'Telefon firmy',
    'settings.company_email': 'E-mail firmy',
    'settings.company_tax_number': 'DIČ firmy',
    'settings.bank_name': 'Název banky',
    'settings.bank_account': 'Číslo účtu',
    'settings.invoice_prefix': 'Prefix faktur',
    'settings.payment_terms_default': 'Výchozí platební podmínky',
    'settings.default_language': 'Výchozí jazyk',
    'settings.save_settings': 'Uložit nastavení',
    'settings.language_english': 'Angličtina',
    'settings.language_czech': 'Čeština',

    // === SETTINGS (extended) ===
    'settings.company_details': 'Údaje o firmě',
    'settings.address_section': 'Adresa',
    'settings.contact_section': 'Kontakt',
    'settings.banking_section': 'Bankovní údaje',
    'settings.invoice_defaults': 'Výchozí nastavení faktur',
    'settings.company_city': 'Město',
    'settings.company_country': 'Země',
    'settings.company_website': 'Web',
    'settings.company_registration': 'IČO',
    'settings.bank_swift': 'SWIFT / BIC',
    'settings.invoice_footer': 'Patička faktury',
    'settings.saved_ok': 'Nastavení bylo úspěšně uloženo!',
    'settings.groups': 'Skupiny',
    'settings.add_currency': 'Přidat měnu',
    'settings.edit_currency': 'Upravit měnu',
    'settings.currency_code': 'Kód (např. CZK)',
    'settings.currency_symbol': 'Symbol (např. Kč)',
    'settings.currency_name': 'Název (např. Česká koruna)',
    'settings.currency_decimals': 'Desetinná místa',
    'settings.currency_default': 'Výchozí měna',
    'settings.no_currencies': 'Žádné měny. Přidejte první měnu.',
    'settings.add_rate': 'Přidat kurz',
    'settings.from_currency': 'Z měny',
    'settings.to_currency': 'Na měnu',
    'settings.rate': 'Kurz',
    'settings.effective_date': 'Datum platnosti',
    'settings.no_rates': 'Žádné kurzy.',
    'settings.min_currencies': 'Přidejte alespoň 2 měny na kartě Měny před přidáním kurzů.',
    'settings.delete_currency_confirm': 'Smazat tuto měnu?',
    'settings.delete_rate_confirm': 'Smazat tento kurz?',

    // === GROUPS ===
    'groups.supplier_groups': 'Skupiny dodavatelů',
    'groups.customer_groups': 'Skupiny zákazníků',
    'groups.new_group': 'Nová skupina',
    'groups.edit_group': 'Upravit skupinu',
    'groups.group_code': 'Kód skupiny',
    'groups.group_name': 'Název / Popis skupiny',
    'groups.delete_confirm': 'Opravdu chcete smazat tuto skupinu?',
    'groups.no_groups': 'Žádné skupiny.',

    // === NUMBER SEQUENCES ===
    'settings.number_sequences': 'Číselné řady',
    'settings.dimensions': 'Finanční dimenze',
    'settings.companies': 'Firmy',
    'company.code': 'Kód',
    'company.name': 'Název',
    'company.new': 'Nová firma',
    'company.edit': 'Upravit firmu',
    'company.city': 'Město',
    'company.country': 'Země',
    'company.address': 'Adresa',
    'company.phone': 'Telefon',
    'company.email': 'E-mail',
    'company.website': 'Web',
    'company.tax_id': 'DIČ',
    'company.registration_number': 'IČO',
    'company.bank_name': 'Název banky',
    'company.bank_account': 'Bankovní účet',
    'company.bank_swift': 'SWIFT/BIC',
    'company.current': 'Aktuální',
    'company.switch': 'Přepnout',
    'company.set_default': 'Nastavit jako výchozí',
    'company.default_set': 'Výchozí firma aktualizována',
    'company.cannot_delete_current': 'Nelze smazat aktuální firmu',
    'number_sequences.title': 'Číselné řady',
    'number_sequences.new': 'Nová řada',
    'number_sequences.edit': 'Upravit řadu',
    'number_sequences.type': 'Typ dokumentu',
    'number_sequences.prefix': 'Předpona',
    'number_sequences.suffix': 'Přípona',
    'number_sequences.next_number': 'Další číslo',
    'number_sequences.padding': 'Výplň',
    'number_sequences.padding_help': 'Počet číslic (např. 5 → 00001)',
    'number_sequences.include_year': 'Včetně roku',
    'number_sequences.include_month': 'Včetně měsíce',
    'number_sequences.year': 'Rok',
    'number_sequences.month': 'Měsíc',
    'number_sequences.preview': 'Náhled',
    'number_sequences.empty': 'Žádné číselné řady. Dokumenty budou používat výchozí číslování.',
    'number_sequences.auto_generate': 'Ponechte prázdné pro automatické číslování',
    'number_sequences.types.customer': 'Zákazník',
    'number_sequences.types.supplier': 'Dodavatel',
    'number_sequences.types.sales_order': 'Prodejní objednávka',
    'number_sequences.types.purchase_order': 'Nákupní objednávka',
    'number_sequences.types.sales_invoice': 'Vydaná faktura',
    'number_sequences.types.purchase_invoice': 'Přijatá faktura',
    'number_sequences.types.goods_receipt': 'Příjemka',
    'number_sequences.types.journal_entry': 'Účetní zápis',

    // === FINANCIAL DIMENSIONS ===
    'dimensions.title': 'Finanční dimenze',
    'dimensions.new': 'Nová dimenze',
    'dimensions.edit': 'Upravit dimenzi',
    'dimensions.values_label': 'Hodnoty',
    'dimensions.new_value': 'Nová hodnota',
    'dimensions.edit_value': 'Upravit hodnotu',
    'dimensions.no_values': 'Žádné hodnoty',
    'dimensions.empty': 'Žádné finanční dimenze.',
    'dimensions.required': 'Povinná',
    'dimensions.sort_order': 'Pořadí řazení',

    // === POSTING PROFILES ===
    'posting.title': 'Účtovací profily',
    'posting.party_profiles': 'Profily zákazníků a dodavatelů',
    'posting.product_profiles': 'Profily produktů',
    'posting.product_posting_profile': 'Účtovací profil produktu',
    'posting.new': 'Nový účtovací profil',
    'posting.edit': 'Upravit účtovací profil',
    'posting.party_type': 'Typ partnera',
    'posting.document_type': 'Typ dokladu',
    'posting.profile_name': 'Název profilu',
    'posting.is_default': 'Výchozí',
    'posting.customer_group': 'Skupina zákazníků (volitelné)',
    'posting.supplier_group': 'Skupina dodavatelů (volitelné)',
    'posting.receivable_account': 'Účet pohledávek',
    'posting.payable_account': 'Účet závazků',
    'posting.advance_account': 'Účet záloh',
    'posting.revenue_account': 'Účet výnosů',
    'posting.expense_account': 'Účet nákladů',
    'posting.tax_account': 'Účet DPH',
    'posting.vat_account': 'Účet DPH',
    'posting.bank_account': 'Bankovní účet',
    'posting.no_profiles': 'Žádné účtovací profily partnerů.',
    'posting.no_product_profiles': 'Žádné účtovací profily produktů.',
    'posting.delete_confirm': 'Smazat tento účtovací profil?',
    'posting.sales_invoice': 'Faktura vydaná',
    'posting.purchase_invoice': 'Faktura přijatá',
    'posting.payment_received': 'Přijatá platba',
    'posting.payment_made': 'Vydaná platba',
    'posting.post_to_ledger': 'Zaúčtovat',
    'posting.post_confirm': 'Zaúčtovat tuto fakturu? Bude vytvořen účetní zápis.',
    'posting.posted_ok': 'Úspěšně zaúčtováno',
    'posting.already_posted': 'Již zaúčtováno',

    // === SUBLEDGER ===
    'subledger.title': 'Pomocná evidence',
    'subledger.entries': 'Záznamy',
    'subledger.balances': 'Zůstatky',
    'subledger.aging': 'Přehled splatnosti',
    'subledger.party_type': 'Typ partnera',
    'subledger.party': 'Partner',
    'subledger.document': 'Doklad',
    'subledger.entry_type': 'Typ záznamu',
    'subledger.debit': 'Má dáti',
    'subledger.credit': 'Dal',
    'subledger.running_balance': 'Zůstatek',
    'subledger.customer': 'Zákazník',
    'subledger.supplier': 'Dodavatel',
    'subledger.all': 'Vše',
    'subledger.no_entries': 'Žádné záznamy.',
    'subledger.total_debit': 'Celkem Má dáti',
    'subledger.total_credit': 'Celkem Dal',
    'subledger.net_balance': 'Čistý zůstatek',
    'subledger.transactions': 'Transakcí',
    'subledger.current': 'Aktuální',
    'subledger.days_1_30': '1-30 dní',
    'subledger.days_31_60': '31-60 dní',
    'subledger.days_61_90': '61-90 dní',
    'subledger.over_90': 'Nad 90 dní',
    'subledger.no_aging': 'Žádné neuhrazené zůstatky.',
  },
};

export default translations;
